/**
 * @module compactNumber
 */
import { roundDecimal } from '../utils';
import { DEFAULT_NOTCHES, getCurrencyConfig, getNotches } from '../config';
import {
  formatCurrency,
  FormatCurrencyNumberOptions,
  _addCurrencySymbols,
} from '../formatCurrency';
import {
  CountryCode,
  Notch,
  Code,
  Precision,
  ParsableNumber,
  LanguageCode,
} from '../typings';
// import { formatNumber } from 'src/formatNumber';
/**
 * Format a big number to simpler style in human language.
 * @private
 * @since 	2.0.0
 * @param 	{number|string} input
 * @param 	{?number|false} maxPrecision=1 Decimal places to keep after formatting.
 * @param 	{Notch[]} notches=DEFAULT_NOTCHES The compact calculation configuration. For exp., [{value: 1000, unit: 'k'}] will make 10,000 to 10k. Since 1.2.3
 * @returns {{number: number, unit: string}}
 */
export function _compactNumber(
  input: ParsableNumber,
  maxPrecision: Precision = 1,
  notches: Notch[] = DEFAULT_NOTCHES
): { number: number; unit: string } {
  const parsedNumber = parseFloat(String(input));
  if (
    isFinite(parsedNumber) &&
    notches &&
    notches.length &&
    parsedNumber >= notches[notches.length - 1].notch
  ) {
    for (let i = notches.length; i--; ) {
      const { notch, unit } = notches[i];
      const nextNotch = notches[i - 1];
      const number = parseFloat(
        roundDecimal(parsedNumber / notch, maxPrecision)
      );
      if (!nextNotch || Math.abs(number) < nextNotch.notch / notch) {
        return {
          number,
          unit,
        };
      }
    }
  }

  return {
    number: parseFloat(roundDecimal(parsedNumber, maxPrecision)),
    unit: '',
  };
}

/**
 * Localize and compact a number.
 * @since  	2.0.0
 * @param   {string} code Can be country code or currency ID. If country code is provided, it will return this country's default currency setting.
 * @param 	{?number} input
 * @param 	{?number|false|module:formatCurrency~FormatCurrencyNumberOptions} options=1 Decimal places to keep after formatting.
 * @param 	{Notch[]} notches=getNotches(code) The compact calculation configuration, it must be written in sequence from the largest unit to the smallest one.. For exp., [{value: 1000, unit: 'k'}] will make 10,000 to 10k. Since 1.2.3
 * @return 	{string}
 * @example
 * import { compactCurrency } from '@shopee_common/currency';
 *
 * // Shorten with country default settings
 * compactCurrency('SG', -12700000); // '-$12.7m'
 * compactCurrency('ID', 12700000); // 'Rp12.7rb'
 *
 * // Specify the maximum allowed precision without trailing 0
 * compactCurrency('SG', -12700000, 0); // '-$13m'
 * compactCurrency('SG', -12700000, 2); // '-$12.7m'
 *
 * // Format without controlling number of decimal digits
 * compactCurrency('SG', 12345.6, false); // '$12.3456k'
 *
 * // Shorten the number and control number of decimal digits, with trailing zero:
 * compactCurrency('SG', -12700000, { precision: 3 }); // '-$12.700m'
 *
 * // Shorten the number without the currency symbol
 * compactCurrency('SG', -12700000, { symbol: '' }); // '-12.7m'
 *
 * // Shorten the number with customized units (notch)
 * compactCurrency('TW', 123456, 1, [
 *   { notch: 100000000, unit: '亿'},
 *  { notch: 10000, unit: '万'},
 *   { notch: 1000, unit: '千'},
 *   { notch: 100, unit: '百'}
 * ]); // ¥12.3万
 */
export function compactCurrency(
  // REVIEW(jennie): pass CountryCode
  code: Code,
  input: ParsableNumber,
  options?: FormatCurrencyNumberOptions,
  notches?: Notch[],
  language?: LanguageCode
): string {
  const maxPrecision =
    options && typeof options === 'object' ? options.maxPrecision : options;
  const precision =
    options && typeof options === 'object' && options.precision != null
      ? options.precision
      : false;

  const parsedNumber = parseFloat(String(input));
  const isNegative = parsedNumber < 0;

  const { number, unit } = _compactNumber(
    Math.abs(parsedNumber),
    typeof precision === 'number'
      ? typeof maxPrecision === 'number'
        ? Math.min(precision, maxPrecision)
        : precision
      : maxPrecision,
    notches || getNotches(code, language)
  );

  const opts: FormatCurrencyNumberOptions =
    options && typeof options === 'object'
      ? { ...options, precision }
      : { precision: false, maxPrecision };

  const { separatorPatternCompact } = getCurrencyConfig(code, language);
  const num = formatCurrency(code, number, {
    ...opts,
    symbol: '',
    separatorPattern: separatorPatternCompact,
  });
  if (opts.symbol === '') {
    return (isNegative ? '-' : '') + num + unit;
  } else {
    return _addCurrencySymbols(num + unit, isNegative, code, opts);
  }
}

/**
 * Format a big number to simpler style in human language.
 * @since 	1.1.0
 * @param 	{number|string} input
 * @param 	{?number|false} maxPrecision=1 Decimal places to keep after formatting.
 * @param 	{Notch[]} notches=DEFAULT_NOTCHES The compact calculation configuration. For exp., [{value: 1000, unit: 'k'}] will make 10,000 to 10k. Since 1.2.3
 * @returns {string}
 * @example
 * // Default maxPrecision is 1
 * compactNumber(12345.6);                     // '12.3k'
 *
 * // Specify maxPrecision
 * compactNumber(12345.6, 2);                  // '12.35k'

 * // Format without controlling number of deicimal digits(precision)
 * compactNumber(12345.6, false);              // '12.3456k'
 * 
 * // Format with customized configurations
 * compactNumber(123456, 0, [
 *  { notch: 100000000, unit: '亿'},
 *  { notch: 10000, unit: '万'},
 *  { notch: 1000, unit: '千'},
 *  { notch: 100, unit: '百'}
 * ]);                                          // '12万'
 */
export function compactNumber(
  input: ParsableNumber,
  maxPrecision: Precision = 1,
  notches: Notch[] = DEFAULT_NOTCHES
): string {
  const { number, unit } = _compactNumber(input, maxPrecision, notches);
  return `${number}${unit}`;
}
/**
 * Compact number with locale settings. Can be replaced by `compactCurrency(country, input, options)`, since *2.0.0-beta.9*.
 * @private
 * @deprecated
 * @since 	1.3.0
 * @param    {string} country Country code
 * @param    {*} input
 * @param    {?number|false|module:formatCurrency~FormatCurrencyNumberOptions} options=1 Decimal places to keep after formatting.
 * @returns  {string}
 * @example
 * localizeCompactNumber('ID', 123456);     // '123.5rb'
 * localizeCompactNumber('ID', 123456, 2);  // '123.46rb'
 */
export function localizeCompactNumber(
  country: CountryCode,
  input: ParsableNumber,
  options?: FormatCurrencyNumberOptions,
  language?: LanguageCode
): string {
  const notches = getNotches(country, language);
  return compactCurrency(
    country,
    input,
    options && typeof options === 'object'
      ? {
          ...options,
          symbol: '',
        }
      : {
          maxPrecision: options,
          symbol: '',
        },
    notches
  );
}
